import type { StaffKey } from './staffText'
import type { ApprovalSettings } from './types'

/**
 * Settings › Approvals: the pure part.
 *
 * Every refusal here is one org.ApprovalSettings.Validate makes too, so a save
 * that would be refused can be caught before the round trip; the server still
 * has the last word (net/mail.ParseAddress may disagree with the regex below).
 */

export const MAX_APPROVAL_EMAILS = 5

// Not RFC 5322: net/mail.ParseAddress on the server is the real check.
const LOOKS_LIKE_AN_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** Whether Save is offered: a change, nothing wrong with it, not mid-save. */
export function approvalsSavable(at: { busy: boolean; unsaved: boolean; problem: StaffKey | '' }): boolean {
  return !at.busy && at.unsaved && at.problem === ''
}

/** What is wrong with each typed address, in order: '' for a fine or blank one
 *  (a blank is dropped on save), else the first problem. */
export function emailProblems(emails: readonly string[]): Array<StaffKey | ''> {
  const seen = new Set<string>()
  return emails.map((typed) => {
    const email = typed.trim()
    if (!email) return ''
    if (!LOOKS_LIKE_AN_EMAIL.test(email)) return 'admin.approvals.badEmail'
    const lower = email.toLowerCase()
    if (seen.has(lower)) return 'admin.approvals.duplicateEmail'
    seen.add(lower)
    return ''
  })
}

/** The first thing wrong with a draft, or '' when it may be saved. */
export function approvalSettingsProblem(settings: ApprovalSettings): StaffKey | '' {
  if (settings.emails.length > MAX_APPROVAL_EMAILS) return 'admin.approvals.tooManyEmails'
  return emailProblems(settings.emails).find(Boolean) ?? ''
}

/** What a save sends: addresses trimmed, blanks dropped. Lower-casing is the
 *  server's (handleSaveApprovalSettings), so the audit "before" reads as typed. */
export function settingsToSend(settings: ApprovalSettings): ApprovalSettings {
  return {
    emails: settings.emails.map((e) => e.trim()).filter(Boolean),
    needsApproval: settings.needsApproval,
  }
}
